'use client';

import { c as _c } from "react/compiler-runtime";
import { SiAstro, SiBiome, SiBower, SiBun, SiC, SiCircleci, SiCoffeescript, SiCplusplus, SiCss, SiCssmodules, SiDart, SiDocker, SiDocusaurus, SiDotenv, SiEditorconfig, SiEslint, SiGatsby, SiGitignoredotio, SiGnubash, SiGo, SiGraphql, SiGrunt, SiGulp, SiHandlebarsdotjs, SiHtml5, SiJavascript, SiJest, SiJson, SiLess, SiMarkdown, SiMdx, SiMintlify, SiMocha, SiMysql, SiNextdotjs, SiPerl, SiPhp, SiPostcss, SiPrettier, SiPrisma, SiPug, SiPython, SiR, SiReact, SiReadme, SiRedis, SiRemix, SiRive, SiRollupdotjs, SiRuby, SiSanity, SiSass, SiScala, SiSentry, SiShadcnui, SiStorybook, SiStylelint, SiSublimetext, SiSvelte, SiSvg, SiSwift, SiTailwindcss, SiToml, SiTypescript, SiVercel, SiVite, SiVuedotjs, SiWebassembly } from '@icons-pack/react-simple-icons';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components';
import { Button } from '@repo/ui/components';
import { cn } from '@repo/ui/lib';
import { transformerNotationDiff, transformerNotationErrorLevel, transformerNotationFocus, transformerNotationHighlight, transformerNotationWordHighlight } from '@shikijs/transformers';
import { CheckIcon, CopyIcon } from 'lucide-react';
import { cloneElement, createContext, useContext, useEffect, useState } from 'react';
import { codeToHtml } from 'shiki';
const filenameIconMap = {
  '*.astro': SiAstro,
  '*.c': SiC,
  '*.coffee': SiCoffeescript,
  '*.cpp': SiCplusplus,
  '*.css': SiCss,
  '*.dart': SiDart,
  '*.go': SiGo,
  '*.graphql': SiGraphql,
  '*.hbs': SiHandlebarsdotjs,
  '*.html': SiHtml5,
  '*.js': SiJavascript,
  '*.json': SiJson,
  '*.jsx': SiReact,
  '*.less': SiLess,
  '*.md': SiMarkdown,
  '*.mdx': SiMdx,
  '*.module.css': SiCssmodules,
  '*.mustache': SiHandlebarsdotjs,
  '*.php': SiPhp,
  '*.pl': SiPerl,
  '*.prisma': SiPrisma,
  '*.pug': SiPug,
  '*.py': SiPython,
  '*.r': SiR,
  '*.rb': SiRuby,
  '*.rdb': SiRedis,
  '*.riv': SiRive,
  '*.sass': SiSass,
  '*.sc': SiScala,
  '*.scala': SiScala,
  '*.scss': SiSass,
  '*.sh': SiGnubash,
  '*.sql': SiMysql,
  '*.svelte': SiSvelte,
  '*.svg': SiSvg,
  '*.swift': SiSwift,
  '*.test.js': SiJest,
  '*.toml': SiToml,
  '*.ts': SiTypescript,
  '*.tsx': SiReact,
  '*.vue': SiVuedotjs,
  '*.wasm': SiWebassembly,
  '.bowerrc': SiBower,
  '.circleci/config.yml': SiCircleci,
  '.editorconfig': SiEditorconfig,
  '.env': SiDotenv,
  '.eslintrc': SiEslint,
  '.gitignore': SiGitignoredotio,
  '.sublime-settings': SiSublimetext,
  'biome.json': SiBiome,
  'bun.lockb': SiBun,
  'components.json': SiShadcnui,
  Dockerfile: SiDocker,
  'docusaurus.config.js': SiDocusaurus,
  'eslint.config.*': SiEslint,
  'gatsby-config.*': SiGatsby,
  'Gruntfile.*': SiGrunt,
  'gulpfile.*': SiGulp,
  'mintlify.json': SiMintlify,
  'mocha.opts': SiMocha,
  'next.config.*': SiNextdotjs,
  'postcss.config.*': SiPostcss,
  'prettier.config.*': SiPrettier,
  'readme.md': SiReadme,
  'remix.config.*': SiRemix,
  'rollup.config.*': SiRollupdotjs,
  'sanity.config.*': SiSanity,
  'sentry.client.config.*': SiSentry,
  'storybook.config.*': SiStorybook,
  'stylelint.config.*': SiStylelint,
  'tailwind.config.*': SiTailwindcss,
  'vercel.json': SiVercel,
  'vite.config.*': SiVite
};
const lineNumberClassNames = cn('[&_code]:[counter-reset:line]', '[&_code]:[counter-increment:line_0]', '[&_.line]:before:content-[counter(line)]', '[&_.line]:before:inline-block', '[&_.line]:before:[counter-increment:line]', '[&_.line]:before:w-4', '[&_.line]:before:mr-4', '[&_.line]:before:text-[13px]', '[&_.line]:before:text-right', '[&_.line]:before:text-muted-foreground/50', '[&_.line]:before:font-mono', '[&_.line]:before:select-none');
const darkModeClassNames = cn('dark:[&_.shiki]:!text-[var(--shiki-dark)]', 'dark:[&_.shiki]:!bg-[var(--shiki-dark-bg)]', 'dark:[&_.shiki]:![font-style:var(--shiki-dark-font-style)]', 'dark:[&_.shiki]:![font-weight:var(--shiki-dark-font-weight)]', 'dark:[&_.shiki]:![text-decoration:var(--shiki-dark-text-decoration)]', 'dark:[&_.shiki_span]:!text-[var(--shiki-dark)]', 'dark:[&_.shiki_span]:![font-style:var(--shiki-dark-font-style)]', 'dark:[&_.shiki_span]:![font-weight:var(--shiki-dark-font-weight)]', 'dark:[&_.shiki_span]:![text-decoration:var(--shiki-dark-text-decoration)]');
const lineHighlightClassNames = cn('[&_.line.highlighted]:bg-blue-50', '[&_.line.highlighted]:after:bg-blue-500', '[&_.line.highlighted]:after:absolute', '[&_.line.highlighted]:after:left-0', '[&_.line.highlighted]:after:top-0', '[&_.line.highlighted]:after:bottom-0', '[&_.line.highlighted]:after:w-0.5', 'dark:[&_.line.highlighted]:!bg-blue-500/10');
const lineDiffClassNames = cn('[&_.line.diff]:after:absolute', '[&_.line.diff]:after:left-0', '[&_.line.diff]:after:top-0', '[&_.line.diff]:after:bottom-0', '[&_.line.diff]:after:w-0.5', '[&_.line.diff.add]:bg-emerald-50', '[&_.line.diff.add]:after:bg-emerald-500', '[&_.line.diff.remove]:bg-rose-50', '[&_.line.diff.remove]:after:bg-rose-500', 'dark:[&_.line.diff.add]:!bg-emerald-500/10', 'dark:[&_.line.diff.remove]:!bg-rose-500/10');
const lineFocusedClassNames = cn('[&_code:has(.focused)_.line]:blur-[2px]', '[&_code:has(.focused)_.line.focused]:blur-none');
const wordHighlightClassNames = cn('[&_.highlighted-word]:bg-blue-50', 'dark:[&_.highlighted-word]:!bg-blue-500/10');
const codeBlockClassName = cn('mt-0 bg-background text-sm', '[&_pre]:py-4', '[&_.shiki]:!bg-[var(--shiki-bg)]', '[&_code]:w-full', '[&_code]:grid', '[&_code]:overflow-x-auto', '[&_code]:bg-transparent', '[&_.line]:px-4', '[&_.line]:w-full', '[&_.line]:relative');
const highlight = (html, language, themes) => codeToHtml(html, {
  lang: language ?? 'typescript',
  themes: themes ?? {
    dark: 'github-dark-default',
    light: 'github-light'
  },
  transformers: [transformerNotationDiff({
    matchAlgorithm: 'v3'
  }), transformerNotationHighlight({
    matchAlgorithm: 'v3'
  }), transformerNotationWordHighlight({
    matchAlgorithm: 'v3'
  }), transformerNotationFocus({
    matchAlgorithm: 'v3'
  }), transformerNotationErrorLevel({
    matchAlgorithm: 'v3'
  })]
});
const CodeBlockContext = createContext({
  data: [],
  onValueChange: undefined,
  value: undefined
});
export const CodeBlock = t0 => {
  const $ = _c(23);
  let className;
  let controlledOnValueChange;
  let controlledValue;
  let data;
  let defaultValue;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      data,
      defaultValue,
      onValueChange: controlledOnValueChange,
      value: controlledValue,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = controlledOnValueChange;
    $[3] = controlledValue;
    $[4] = data;
    $[5] = defaultValue;
    $[6] = props;
  } else {
    className = $[1];
    controlledOnValueChange = $[2];
    controlledValue = $[3];
    data = $[4];
    defaultValue = $[5];
    props = $[6];
  }
  const t1 = defaultValue ?? "";
  let t2;
  if ($[7] !== controlledOnValueChange || $[8] !== controlledValue || $[9] !== t1) {
    t2 = {
      defaultProp: t1,
      onChange: controlledOnValueChange,
      prop: controlledValue
    };
    $[7] = controlledOnValueChange;
    $[8] = controlledValue;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const [value, onValueChange] = useControllableState(t2);
  let t3;
  if ($[11] !== data || $[12] !== onValueChange || $[13] !== value) {
    t3 = {
      data,
      onValueChange,
      value
    };
    $[11] = data;
    $[12] = onValueChange;
    $[13] = value;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== className) {
    t4 = cn("size-full overflow-hidden rounded-md border", className);
    $[15] = className;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== props || $[18] !== t4) {
    t5 = <div className={t4} {...props} />;
    $[17] = props;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t5) {
    t6 = <CodeBlockContext.Provider value={t3}>{t5}</CodeBlockContext.Provider>;
    $[20] = t3;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
export const CodeBlockHeader = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-secondary flex flex-row items-center border-b p-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const CodeBlockFiles = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    data
  } = useContext(CodeBlockContext);
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex grow flex-row items-center gap-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== data) {
    t2 = data.map(children);
    $[6] = children;
    $[7] = data;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== t1 || $[11] !== t2) {
    t3 = <div className={t1} {...props}>{t2}</div>;
    $[9] = props;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
export const CodeBlockFilename = t0 => {
  const $ = _c(15);
  let children;
  let icon;
  let props;
  let value;
  if ($[0] !== t0) {
    const {
      children: t1,
      className,
      icon: t2,
      value: t3,
      ...t4
    } = t0;
    children = t1;
    icon = t2;
    value = t3;
    props = t4;
    $[0] = t0;
    $[1] = children;
    $[2] = icon;
    $[3] = props;
    $[4] = value;
  } else {
    children = $[1];
    icon = $[2];
    props = $[3];
    value = $[4];
  }
  const {
    value: activeValue
  } = useContext(CodeBlockContext);
  let t1;
  if ($[5] !== children) {
    t1 = Object.entries(filenameIconMap).find(t2 => {
      const [pattern] = t2;
      const regex = new RegExp(`^${pattern.replace(/\\/g, "\\\\").replace(/\./g, "\\.").replace(/\*/g, ".*")}$`);
      return regex.test(children);
    })?.[1];
    $[5] = children;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const defaultIcon = t1;
  const Icon = icon ?? defaultIcon;
  if (value !== activeValue) {
    return null;
  }
  let t2;
  if ($[7] !== Icon) {
    t2 = Icon && <Icon className="h-4 w-4 shrink-0" />;
    $[7] = Icon;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children) {
    t3 = <span className="flex-1 truncate">{children}</span>;
    $[9] = children;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== t2 || $[13] !== t3) {
    t4 = <div className="bg-secondary text-muted-foreground flex items-center gap-2 px-4 py-1.5 text-xs" {...props}>{t2}{t3}</div>;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const CodeBlockSelect = props => {
  const $ = _c(4);
  const {
    onValueChange,
    value
  } = useContext(CodeBlockContext);
  let t0;
  if ($[0] !== onValueChange || $[1] !== props || $[2] !== value) {
    t0 = <Select onValueChange={onValueChange} value={value} {...props} />;
    $[0] = onValueChange;
    $[1] = props;
    $[2] = value;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const CodeBlockSelectTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-muted-foreground w-fit border-none text-xs shadow-none", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const CodeBlockSelectValue = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <SelectValue {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const CodeBlockSelectContent = t0 => {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    data
  } = useContext(CodeBlockContext);
  let t1;
  if ($[3] !== children || $[4] !== data) {
    t1 = data.map(children);
    $[3] = children;
    $[4] = data;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <SelectContent {...props}>{t1}</SelectContent>;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const CodeBlockSelectItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("text-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const CodeBlockCopyButton = t0 => {
  const $ = _c(27);
  let asChild;
  let children;
  let className;
  let onCopy;
  let onError;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      asChild,
      children,
      className,
      onCopy,
      onError,
      timeout: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = onCopy;
    $[5] = onError;
    $[6] = props;
    $[7] = t1;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    onCopy = $[4];
    onError = $[5];
    props = $[6];
    t1 = $[7];
  }
  const timeout = t1 === undefined ? 2000 : t1;
  const [isCopied, setIsCopied] = useState(false);
  const {
    data,
    value
  } = useContext(CodeBlockContext);
  let copyToClipboard;
  let t2;
  if ($[8] !== asChild || $[9] !== children || $[10] !== data || $[11] !== onCopy || $[12] !== onError || $[13] !== timeout || $[14] !== value) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const code = data.find(item => item.language === value)?.code;
      copyToClipboard = () => {
        if (typeof window === "undefined" || !navigator.clipboard.writeText || !code) {
          return;
        }
        navigator.clipboard.writeText(code).then(() => {
          setIsCopied(true);
          onCopy?.();
          setTimeout(() => setIsCopied(false), timeout);
        }, onError);
      };
      if (asChild) {
        t2 = cloneElement(children, {
          onClick: copyToClipboard
        });
        break bb0;
      }
    }
    $[8] = asChild;
    $[9] = children;
    $[10] = data;
    $[11] = onCopy;
    $[12] = onError;
    $[13] = timeout;
    $[14] = value;
    $[15] = copyToClipboard;
    $[16] = t2;
  } else {
    copyToClipboard = $[15];
    t2 = $[16];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const Icon = isCopied ? CheckIcon : CopyIcon;
  let t3;
  if ($[17] !== className) {
    t3 = cn("shrink-0", className);
    $[17] = className;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  let t4;
  if ($[19] !== Icon || $[20] !== children) {
    t4 = children ?? <Icon className="text-muted-foreground" size={14} />;
    $[19] = Icon;
    $[20] = children;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] !== copyToClipboard || $[23] !== props || $[24] !== t3 || $[25] !== t4) {
    t5 = <Button className={t3} onClick={copyToClipboard} size="icon" variant="ghost" {...props}>{t4}</Button>;
    $[22] = copyToClipboard;
    $[23] = props;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
};
const CodeBlockFallback = t0 => {
  const $ = _c(10);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children) {
    t1 = children?.toString().split("\n").map(_temp);
    $[3] = children;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <pre className="w-full"><code>{t1}</code></pre>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== props || $[8] !== t2) {
    t3 = <div {...props}>{t2}</div>;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export const CodeBlockBody = t0 => {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const {
    data
  } = useContext(CodeBlockContext);
  let t1;
  if ($[3] !== children || $[4] !== data) {
    t1 = data.map(children);
    $[3] = children;
    $[4] = data;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== t1) {
    t2 = <div {...props}>{t1}</div>;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const CodeBlockItem = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      children,
      className,
      lineNumbers: t1,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = value;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    value = $[5];
  }
  const lineNumbers = t1 === undefined ? true : t1;
  const {
    value: activeValue
  } = useContext(CodeBlockContext);
  if (value !== activeValue) {
    return null;
  }
  const t2 = lineNumbers && lineNumberClassNames;
  let t3;
  if ($[6] !== className || $[7] !== t2) {
    t3 = cn(codeBlockClassName, lineHighlightClassNames, lineDiffClassNames, lineFocusedClassNames, wordHighlightClassNames, darkModeClassNames, t2, className);
    $[6] = className;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t3) {
    t4 = <div className={t3} {...props}>{children}</div>;
    $[9] = children;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export const CodeBlockContent = t0 => {
  const $ = _c(19);
  let children;
  let language;
  let props;
  let t1;
  let themes;
  if ($[0] !== t0) {
    ({
      children,
      language,
      syntaxHighlighting: t1,
      themes,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = language;
    $[3] = props;
    $[4] = t1;
    $[5] = themes;
  } else {
    children = $[1];
    language = $[2];
    props = $[3];
    t1 = $[4];
    themes = $[5];
  }
  const syntaxHighlighting = t1 === undefined ? true : t1;
  const [html, setHtml] = useState(null);
  let t2;
  let t3;
  if ($[6] !== children || $[7] !== language || $[8] !== syntaxHighlighting || $[9] !== themes) {
    t2 = () => {
      if (!syntaxHighlighting) {
        return;
      }
      highlight(children, language, themes).then(setHtml).catch(console.error);
    };
    t3 = [children, themes, syntaxHighlighting, language];
    $[6] = children;
    $[7] = language;
    $[8] = syntaxHighlighting;
    $[9] = themes;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  useEffect(t2, t3);
  if (!(syntaxHighlighting && html)) {
    let t4;
    if ($[12] !== children) {
      t4 = <CodeBlockFallback>{children}</CodeBlockFallback>;
      $[12] = children;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  let t4;
  if ($[14] !== html) {
    t4 = {
      __html: html
    };
    $[14] = html;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  let t5;
  if ($[16] !== props || $[17] !== t4) {
    t5 = <div dangerouslySetInnerHTML={t4} {...props} />;
    $[16] = props;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
function _temp(line, i) {
  return <span className="line" key={i}>{line}</span>;
}